import { useEffect, useState } from 'react'
import { BoardLocationPicker, type BoardLocationDraft } from './BoardLocationPicker'
import { boardLocationPreviewIdle, type BoardCamera } from './boardLocation'
import { MIRACLE_HUDSON_ID } from './canned/miracleHudson'
import { YUCCA_GAS_LEAK_ID } from './canned/yuccaSlices'
import { MAP_POSTER_STYLE_ID } from './maps'
import { PosterThumb } from './posters/PosterThumb'
import { SESSION_TYPE_IDS, sessionTypeById, type SessionTypeId } from './sessionTypes'
import type { WizardSessionAnswers } from './types'

export const GET_STARTED_SEEN_KEY = 'sc-get-started-seen'
export const WIZARD_ANSWERS_KEY = 'sc-wizard-answers'

export type BoardComplexity = 'easy' | 'default' | 'everything'

const DEFAULT_COMPLEXITY: BoardComplexity = 'default'

export type WizardIntent = 'looking_around' | SessionTypeId
export type WizardStarter = 'blank' | 'example'

export type WizardAnswers = {
  intent: WizardIntent | null
  typeId: SessionTypeId | null
  starter: WizardStarter | null
}

export type WizardStartResult =
  | {
      kind: 'create'
      typeId: SessionTypeId
      boardComplexity: BoardComplexity
      answers: WizardSessionAnswers
      camera: BoardCamera
    }
  | {
      kind: 'sample'
      typeId: SessionTypeId
      boardComplexity: BoardComplexity
      answers: WizardSessionAnswers
      templateId: string
    }

/** Featured clone per type. Whiteboard has no listed example (factory stays in the library). */
export const WIZARD_EXAMPLE_BY_TYPE: Partial<Record<SessionTypeId, { id: string; title: string }>> = {
  walkthrough: { id: YUCCA_GAS_LEAK_ID, title: 'Residential gas leak' },
  exercise: { id: MIRACLE_HUDSON_ID, title: 'Miracle on the Hudson' },
}

export function wizardFeaturedExample(
  typeId: SessionTypeId | null | undefined,
): { id: string; title: string } | undefined {
  if (!typeId) return undefined
  return WIZARD_EXAMPLE_BY_TYPE[typeId]
}

export function wizardAnswersSnapshot(answers: WizardAnswers): WizardSessionAnswers | null {
  if (!answers.typeId) return null
  return {
    typeId: answers.typeId,
    lookingAround: answers.intent === 'looking_around',
    starter: answers.starter ?? (answers.intent === 'looking_around' ? 'blank' : undefined),
  }
}

export function resolveWizardStart(
  answers: WizardAnswers,
  camera?: BoardCamera | null,
): WizardStartResult | null {
  const snapshot = wizardAnswersSnapshot(answers)
  if (!answers.typeId || !snapshot) return null
  const boardComplexity =
    answers.intent === 'looking_around' ? 'easy' : DEFAULT_COMPLEXITY
  if (answers.starter === 'example') {
    const featured = wizardFeaturedExample(answers.typeId)
    if (featured) {
      return {
        kind: 'sample',
        typeId: answers.typeId,
        boardComplexity,
        answers: snapshot,
        templateId: featured.id,
      }
    }
  }
  if (!camera) return null
  return {
    kind: 'create',
    typeId: answers.typeId,
    boardComplexity,
    answers: snapshot,
    camera,
  }
}

export type WizardDoneMeta = {
  title?: string
}

type Choice = {
  id: string
  label: string
  hint: string
  tone: 'mint' | 'sky' | 'amber' | 'rose' | 'slate'
}

type StepId = 'intent' | 'starter' | 'place'

const INTENT_CHOICES: readonly Choice[] = [
  {
    id: 'looking_around',
    label: 'Just looking around',
    hint: 'Leave me to poke around.',
    tone: 'mint',
  },
  {
    id: 'whiteboard',
    label: 'Whiteboard',
    hint: 'Start a new board. Place objects, discuss, share, or invite people.',
    tone: 'sky',
  },
  {
    id: 'walkthrough',
    label: 'Walkthrough',
    hint: 'Set up a prepared scene with a timeline. You present, others follow along.',
    tone: 'amber',
  },
  {
    id: 'exercise',
    label: 'Exercise',
    hint: 'Build a timed drill with a shared Run, named people, and assigned roles.',
    tone: 'rose',
  },
]

const DEFAULT_INTENT: WizardIntent = 'looking_around'

type Props = {
  open: boolean
  phase: 'pick' | 'done'
  resultType: SessionTypeId | null
  doneMeta: WizardDoneMeta | null
  userKey?: string
  busy: boolean
  canCreate: boolean
  forceArriveOnDismiss: boolean
  dismissLabel?: string
  onStart: (result: WizardStartResult) => void
  onClose: () => void
  onGoToSession: () => void
  onDismissDone: () => void
}

export function markGetStartedSeen(): void {
  try {
    localStorage.setItem(GET_STARTED_SEEN_KEY, '1')
  } catch {
    /* private mode */
  }
}

export function hasSeenGetStarted(): boolean {
  try {
    return localStorage.getItem(GET_STARTED_SEEN_KEY) === '1'
  } catch {
    return true
  }
}

function answersStorageKey(userKey?: string): string {
  const key = userKey?.trim().toLowerCase()
  return key ? `${WIZARD_ANSWERS_KEY}:${key}` : WIZARD_ANSWERS_KEY
}

function isSessionTypeId(value: unknown): value is SessionTypeId {
  return typeof value === 'string' && (SESSION_TYPE_IDS as readonly string[]).includes(value)
}

function isIntent(value: unknown): value is WizardIntent {
  return value === 'looking_around' || isSessionTypeId(value)
}

function typeFromIntent(intent: WizardIntent): SessionTypeId {
  return intent === 'looking_around' ? 'whiteboard' : intent
}

function seedAnswers(intent: WizardIntent, starter?: WizardStarter | null): WizardAnswers {
  const looking = intent === 'looking_around'
  return {
    intent,
    typeId: typeFromIntent(intent),
    starter: looking ? 'blank' : (starter ?? 'blank'),
  }
}

function intentFromLegacySaved(parsed: Record<string, unknown>): WizardIntent | null {
  if (parsed.lookingAround === true || parsed.intent === 'looking_around' || parsed.who === 'browsing') {
    return 'looking_around'
  }
  if (isIntent(parsed.intent)) return parsed.intent
  if (isSessionTypeId(parsed.typeId)) return parsed.typeId
  const task = typeof parsed.task === 'string' ? parsed.task : ''
  const interaction = typeof parsed.interaction === 'string' ? parsed.interaction : ''
  if (task === 'timed_drill' || task === 'roleplay_drill' || interaction === 'roleplay') {
    return 'exercise'
  }
  if (task === 'guided_wt' || interaction === 'spoken_qa') return 'walkthrough'
  if (parsed.who || parsed.task) return 'looking_around'
  return null
}

export function loadWizardAnswerDefaults(userKey?: string): WizardAnswers | null {
  try {
    const raw = localStorage.getItem(answersStorageKey(userKey))
    if (!raw) return null
    const parsed = JSON.parse(raw) as Record<string, unknown>
    if (!parsed || typeof parsed !== 'object') return null
    const intent = intentFromLegacySaved(parsed)
    if (!intent) return null
    const starter =
      parsed.starter === 'example' &&
      intent !== 'looking_around' &&
      wizardFeaturedExample(typeFromIntent(intent))
        ? 'example'
        : 'blank'
    return seedAnswers(intent, starter)
  } catch {
    return null
  }
}

export function saveWizardAnswerDefaults(answers: WizardAnswers, userKey?: string): void {
  if (!answers.intent || !answers.typeId) return
  try {
    localStorage.setItem(
      answersStorageKey(userKey),
      JSON.stringify({
        intent: answers.intent,
        typeId: answers.typeId,
        starter: answers.starter,
        lookingAround: answers.intent === 'looking_around',
        savedAtIso: new Date().toISOString(),
      }),
    )
  } catch {
    /* private mode */
  }
}

function stepsFor(answers: WizardAnswers): StepId[] {
  if (answers.intent === 'looking_around' || !wizardFeaturedExample(answers.typeId)) {
    return ['intent', 'place']
  }
  if (answers.starter === 'example') return ['intent', 'starter']
  return ['intent', 'starter', 'place']
}

function starterChoices(typeId: SessionTypeId): readonly Choice[] {
  const typeLabel = sessionTypeById(typeId)?.label ?? 'board'
  const rows: Choice[] = [
    {
      id: 'blank',
      label: 'Start with a blank board',
      hint: `We’ll set up an empty ${typeLabel} you can work with.`,
      tone: 'sky',
    },
  ]
  if (wizardFeaturedExample(typeId)) {
    rows.push({
      id: 'example',
      label: 'Start with an example',
      hint: 'We’ll create a ready-made scene you can work with.',
      tone: 'amber',
    })
  }
  return rows
}

function typeChoiceLabel(answers: WizardAnswers): string | null {
  if (answers.intent === 'looking_around') return null
  return sessionTypeById(answers.typeId)?.label ?? null
}

function stepHeading(stepId: StepId, answers: WizardAnswers): string {
  if (stepId === 'starter') {
    const typeLabel = typeChoiceLabel(answers)
    return typeLabel ? `Let’s create your ${typeLabel.toLowerCase()}.` : 'Let’s create your board.'
  }
  if (stepId === 'place') return 'Set Your Location'
  return 'Getting Started'
}

function stepIntro(stepId: StepId): string {
  if (stepId === 'place') {
    return 'Sessions always live at a specific place on the map. Where should this session live?'
  }
  if (stepId === 'starter') {
    return 'You have two options here.'
  }
  return 'There’s a lot you can do here. Pick the option that fits you best.'
}

function WizardPreviewArt({ typeId }: { typeId: SessionTypeId | null }) {
  const kind = typeId ?? 'whiteboard'
  const label = typeId ? sessionTypeById(typeId)?.label ?? 'Session' : 'Session'
  return <PosterThumb shotId={`wizard-${kind}`} artClass={kind} caption={label} />
}

export function GettingStartedWizard({
  open,
  phase,
  resultType,
  doneMeta,
  userKey,
  busy,
  canCreate,
  forceArriveOnDismiss,
  dismissLabel = 'Not now',
  onStart,
  onClose,
  onGoToSession,
  onDismissDone,
}: Props) {
  const [answers, setAnswers] = useState<WizardAnswers>(() => seedAnswers(DEFAULT_INTENT))
  const [stepIndex, setStepIndex] = useState(0)
  const [placeCamera, setPlaceCamera] = useState<BoardCamera | null>(null)
  const [placeReady, setPlaceReady] = useState(false)
  const [placeDraft, setPlaceDraft] = useState<BoardLocationDraft | null>(null)

  useEffect(() => {
    if (!open || phase !== 'pick') return
    const defaults = loadWizardAnswerDefaults(userKey)
    setAnswers(defaults ?? seedAnswers(DEFAULT_INTENT))
    setStepIndex(0)
    setPlaceCamera(boardLocationPreviewIdle())
    setPlaceReady(true)
    setPlaceDraft(null)
  }, [open, phase, userKey])

  useEffect(() => {
    if (!open) return
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return
      e.preventDefault()
      if (phase === 'done') {
        if (forceArriveOnDismiss) onGoToSession()
        else onDismissDone()
        return
      }
      onClose()
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [open, phase, onClose, onDismissDone, onGoToSession, forceArriveOnDismiss])

  const steps = stepsFor(answers)
  const stepId = steps[Math.min(stepIndex, steps.length - 1)] ?? 'intent'
  const onPlaceStep = stepId === 'place'
  const lookingAround = answers.intent === 'looking_around'
  const onExampleStart = stepId === 'starter' && answers.starter === 'example'
  const doneProfile = resultType ? sessionTypeById(resultType) : undefined
  const doneKind = (doneProfile?.label ?? 'board').toLowerCase()
  const doneArticle = /^[aeiou]/i.test(doneKind) ? 'an' : 'a'
  const doneNote = doneMeta?.title
    ? `We set up “${doneMeta.title}” for you. Here it is!`
    : `We started ${doneArticle} ${doneKind} for you. Here it is!`

  function clearPlaceDraft(): void {
    setPlaceCamera(boardLocationPreviewIdle())
    setPlaceReady(true)
    setPlaceDraft(null)
  }

  function selectIntent(id: WizardIntent): void {
    setAnswers(seedAnswers(id, id === 'looking_around' ? 'blank' : 'blank'))
    clearPlaceDraft()
  }

  function selectStarter(id: WizardStarter): void {
    setAnswers((prev) => seedAnswers(prev.intent ?? DEFAULT_INTENT, id))
    if (id === 'example') clearPlaceDraft()
  }

  function finish(next: WizardAnswers, camera?: BoardCamera | null): void {
    const result = resolveWizardStart(next, camera)
    if (!result || !canCreate) return
    saveWizardAnswerDefaults(next, userKey)
    onStart(result)
  }

  function goContinue(): void {
    if (busy) return
    if (stepId === 'intent') {
      if (!answers.intent) return
      setStepIndex(1)
      return
    }
    if (stepId === 'starter' && answers.starter === 'example') {
      finish(answers)
      return
    }
    if (stepId === 'starter') {
      setStepIndex((i) => Math.min(i + 1, stepsFor(answers).length - 1))
      return
    }
    if (!placeReady || !placeCamera) return
    finish(answers, placeCamera)
  }

  if (!open) return null

  const isFirst = stepIndex === 0
  const choiceList =
    stepId === 'intent'
      ? INTENT_CHOICES
      : stepId === 'starter' && answers.typeId
        ? starterChoices(answers.typeId)
        : []
  const selectedId =
    stepId === 'intent' ? answers.intent : stepId === 'starter' ? answers.starter : null
  const canContinue = onPlaceStep
    ? placeReady && Boolean(placeCamera) && !busy && canCreate
    : Boolean(selectedId) && !busy && (onExampleStart ? canCreate : true)

  const title = stepHeading(stepId, answers)
  const sub = stepIntro(stepId)

  function dismissDoneBackdrop(): void {
    if (forceArriveOnDismiss) onGoToSession()
    else onDismissDone()
  }

  return (
    <div
      className="sc-modal-back sc-wizard-back"
      onClick={phase === 'done' ? dismissDoneBackdrop : onClose}
    >
      <div
        className={`sc-modal sc-wizard${phase === 'done' ? ' is-done' : ''}${phase === 'pick' && onPlaceStep ? ' sc-wizard-place' : ''}`}
        role="dialog"
        aria-modal="true"
        aria-labelledby="sc-wizard-title"
        onClick={(e) => e.stopPropagation()}
      >
        {phase === 'pick' ? (
          <>
            <div className="sc-wizard-top">
              <div className="sc-wizard-topline">
                <div className="sc-wizard-progress" aria-hidden="true">
                  {steps.map((s, i) => (
                    <span
                      key={s}
                      className={`sc-wizard-dot${i === stepIndex ? ' on' : ''}${i < stepIndex ? ' done' : ''}`}
                    />
                  ))}
                </div>
                <span className="sc-wizard-eta">
                  {onPlaceStep || lookingAround
                    ? 'Est. time: Under a minute'
                    : 'Est. time: About 1 min'}
                </span>
              </div>
              <h2 id="sc-wizard-title">{title}</h2>
              <p className="sc-wizard-sub">{sub}</p>
            </div>
            {onPlaceStep ? (
              <div className="sc-wizard-place-body">
                <BoardLocationPicker
                  key="wizard-place"
                  initialCamera={placeDraft?.camera ?? boardLocationPreviewIdle()}
                  restoreDraft={placeDraft}
                  mapId={MAP_POSTER_STYLE_ID}
                  showActions={false}
                  busy={busy}
                  onCameraChange={(cam, ready) => {
                    setPlaceCamera(cam)
                    setPlaceReady(ready)
                  }}
                  onDraftChange={setPlaceDraft}
                />
              </div>
            ) : (
              <div className="sc-wizard-cards" role="listbox" aria-label={sub}>
                {choiceList.map((row) => {
                  const on = selectedId === row.id
                  return (
                    <button
                      key={row.id}
                      type="button"
                      role="option"
                      aria-selected={on}
                      className={`sc-wizard-card tone-${row.tone}${on ? ' on' : ''}`}
                      onClick={() => {
                        if (stepId === 'intent') selectIntent(row.id as WizardIntent)
                        else selectStarter(row.id as WizardStarter)
                      }}
                      disabled={busy}
                    >
                      <b>{row.label}</b>
                      <span>{row.hint}</span>
                    </button>
                  )
                })}
              </div>
            )}
            <div className="sc-modal-actions">
              <button
                type="button"
                className={`sc-btn${isFirst ? ' sc-wizard-back-slot' : ''}`}
                onClick={() => setStepIndex((i) => Math.max(0, i - 1))}
                disabled={busy || isFirst}
                tabIndex={isFirst ? -1 : undefined}
                aria-hidden={isFirst || undefined}
              >
                Back
              </button>
              <button type="button" className="sc-btn" onClick={onClose} disabled={busy}>
                {dismissLabel}
              </button>
              <button
                type="button"
                className="sc-btn go armed-green"
                disabled={!canContinue}
                onClick={goContinue}
              >
                {busy ? 'Creating…' : onPlaceStep || onExampleStart ? 'Start' : 'Continue'}
              </button>
            </div>
          </>
        ) : (
          <>
            <h2 id="sc-wizard-title" className="sc-wizard-done-title">
              You’re all set
            </h2>
            <p className="sc-wizard-done-note">{doneNote}</p>
            <button
              type="button"
              className="sc-wizard-preview-hit"
              onClick={onGoToSession}
              aria-label={`Open ${doneProfile?.label ?? 'board'} now`}
            >
              <WizardPreviewArt typeId={resultType} />
            </button>
            <div className="sc-modal-actions sc-wizard-done-actions">
              {!forceArriveOnDismiss ? (
                <button type="button" className="sc-btn" onClick={onDismissDone}>
                  Not now
                </button>
              ) : null}
              <button
                type="button"
                className="sc-btn go armed-green sc-wizard-go"
                autoFocus
                onClick={onGoToSession}
              >
                Open it now
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  )
}
